// components/shop/PhotoViewer.jsx
// Full-screen product photo viewer, shared by Uganda's Shop tab (pages/Market.jsx)
// and the DRC shop (pages/DwiSendShop.jsx), so buyers can read labels. Display
// only — it shows the photos the product already has and changes nothing.
//   phone     pinch to zoom, drag a zoomed photo, double-tap to zoom in/out,
//             swipe left/right between photos
//   computer  wheel / trackpad to zoom, drag a zoomed photo, double-click,
//             ← → keys, + − buttons and keys
//   close     X button, Escape, or a tap outside the photo
// No library: pointer events + one CSS transform (translate, then scale).
import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';

export const MAX_ZOOM = 5;
const STEP_ZOOM   = 2.5;  // double-tap / double-click / + button
const SWIPE_PX    = 50;   // finger travel that counts as a swipe to the next photo
const TAP_PX      = 8;    // less than this is a tap, not a drag
const DOUBLE_MS   = 300;

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const FIT = { s: 1, x: 0, y: 0 };

// Zoom to `scale` keeping the point (px, py) — measured from the middle of the
// screen — where it is under the finger / cursor.
export function zoomAt(view, scale, px, py) {
  const s = clamp(scale, 1, MAX_ZOOM);
  if (s === 1) return FIT;
  const k = s / view.s;
  return { s, x: px - (px - view.x) * k, y: py - (py - view.y) * k };
}

// Keep a zoomed photo covering the screen: no dragging it off into black space.
// w/h = photo size on screen at zoom 1, W/H = screen area size.
export function clampPan(view, w, h, W, H) {
  if (view.s <= 1) return FIT;
  const bx = Math.max(0, (view.s * w - W) / 2), by = Math.max(0, (view.s * h - H) / 2);
  return { s: view.s, x: clamp(view.x, -bx, bx), y: clamp(view.y, -by, by) };
}

const ctrl = 'flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80 disabled:opacity-30';

export function PhotoViewer({ photos, start = 0, alt = '', onClose, labels = {} }) {
  const L = { close: 'Close', prev: 'Previous photo', next: 'Next photo', zoomIn: 'Zoom in', zoomOut: 'Zoom out', ...labels };
  const n = photos.length;
  const [index, setIndex]   = useState(() => clamp(start, 0, n - 1));
  const [view, setView]     = useState(FIT);
  const [swipe, setSwipe]   = useState(0);       // px the photo follows the finger while swiping
  const [touching, setTouching] = useState(false);
  const stageRef = useRef(null);
  const imgRef   = useRef(null);
  const viewRef  = useRef(view);
  viewRef.current = view;
  const closeRef = useRef(onClose);   // pages pass a new arrow each render
  closeRef.current = onClose;
  const close = useCallback(() => closeRef.current(), []);
  const g = useRef({ pts: new Map(), start: null, pinch: null, pinched: false, moved: false, lastTap: null });

  // A point on screen, measured from the middle of the photo area.
  const fromCentre = useCallback((x, y) => {
    const r = stageRef.current.getBoundingClientRect();
    return { x: x - (r.left + r.width / 2), y: y - (r.top + r.height / 2) };
  }, []);
  const fit = useCallback(v => {
    const img = imgRef.current, st = stageRef.current;
    return img && st ? clampPan(v, img.offsetWidth, img.offsetHeight, st.clientWidth, st.clientHeight) : v;
  }, []);
  const go = useCallback(d => { if (n > 1) { setIndex(i => (i + d + n) % n); setView(FIT); } }, [n]);
  const zoomBy = useCallback(f => setView(v => fit(zoomAt(v, v.s * f, 0, 0))), [fit]);

  // Keys, and no page scrolling behind the viewer while it is open.
  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') { e.stopPropagation(); close(); }
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === '+' || e.key === '=') zoomBy(STEP_ZOOM);
      else if (e.key === '-') zoomBy(1 / STEP_ZOOM);
    };
    window.addEventListener('keydown', onKey, true);   // capture: Escape closes only the viewer
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey, true); document.body.style.overflow = overflow; };
  }, [close, go, zoomBy]);

  // Wheel / trackpad zoom (needs a non-passive listener to stop the page zooming),
  // and older iPhone Safari's own pinch gesture.
  useEffect(() => {
    const st = stageRef.current;
    const onWheel = e => {
      e.preventDefault();
      const p = fromCentre(e.clientX, e.clientY);
      const f = Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.002));   // ctrlKey = trackpad pinch
      setView(v => fit(zoomAt(v, v.s * f, p.x, p.y)));
    };
    const stop = e => e.preventDefault();
    st.addEventListener('wheel', onWheel, { passive: false });
    st.addEventListener('gesturestart', stop);
    return () => { st.removeEventListener('wheel', onWheel); st.removeEventListener('gesturestart', stop); };
  }, [fromCentre, fit]);

  const twoPoints = () => [...g.current.pts.values()].slice(0, 2);
  const restartFrom = p => { g.current.start = { x: p.x, y: p.y, t: Date.now(), view: viewRef.current, onImg: true }; };

  const down = e => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const s = g.current;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    s.pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    setTouching(true);
    if (s.pts.size === 1) {
      s.start = { x: e.clientX, y: e.clientY, t: Date.now(), view: viewRef.current, onImg: e.target === imgRef.current };
      s.moved = false; s.pinched = false;
    } else if (s.pts.size === 2) {
      const [a, b] = twoPoints();
      s.pinched = true;
      s.pinch = { d: Math.hypot(a.x - b.x, a.y - b.y) || 1, mid: fromCentre((a.x + b.x) / 2, (a.y + b.y) / 2), view: viewRef.current };
      setSwipe(0);
    }
  };

  const move = e => {
    const s = g.current;
    if (!s.pts.has(e.pointerId)) return;
    s.pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (s.pts.size >= 2) {
      const [a, b] = twoPoints();
      const mid = fromCentre((a.x + b.x) / 2, (a.y + b.y) / 2);
      const v = zoomAt(s.pinch.view, s.pinch.view.s * Math.hypot(a.x - b.x, a.y - b.y) / s.pinch.d, s.pinch.mid.x, s.pinch.mid.y);
      setView(fit(v.s > 1 ? { s: v.s, x: v.x + mid.x - s.pinch.mid.x, y: v.y + mid.y - s.pinch.mid.y } : v));
      return;
    }
    const dx = e.clientX - s.start.x, dy = e.clientY - s.start.y;
    if (Math.abs(dx) > TAP_PX || Math.abs(dy) > TAP_PX) s.moved = true;
    if (s.start.view.s > 1) setView(fit({ s: s.start.view.s, x: s.start.view.x + dx, y: s.start.view.y + dy }));
    else if (!s.pinched && n > 1) setSwipe(dx);
  };

  const up = e => {
    const s = g.current;
    if (!s.pts.has(e.pointerId)) return;
    s.pts.delete(e.pointerId);
    if (s.pts.size === 1) { restartFrom([...s.pts.values()][0]); return; }   // pinch over, one finger left: drag
    if (s.pts.size) return;
    setTouching(false);
    setSwipe(0);
    const st = s.start;
    if (!st || s.pinched) return;
    const dx = e.clientX - st.x, dy = e.clientY - st.y;
    if (!s.moved) {
      if (!st.onImg) { close(); return; }                                 // tap outside the photo
      const prev = s.lastTap, now = Date.now();
      if (prev && now - prev.t < DOUBLE_MS && Math.hypot(e.clientX - prev.x, e.clientY - prev.y) < 30) {
        s.lastTap = null;
        const p = fromCentre(e.clientX, e.clientY);
        setView(v => (v.s > 1 ? FIT : fit(zoomAt(v, STEP_ZOOM, p.x, p.y))));
      } else s.lastTap = { t: now, x: e.clientX, y: e.clientY };
    } else if (st.view.s === 1 && Math.abs(dx) > SWIPE_PX && Math.abs(dx) > Math.abs(dy)) {
      go(dx < 0 ? 1 : -1);
    }
  };

  const zoomed = view.s > 1;
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={alt} className="fixed inset-0 z-[100] bg-black/95 select-none">
      <div ref={stageRef} className="absolute inset-0 flex items-center justify-center overflow-hidden"
        style={{ touchAction: 'none', cursor: zoomed ? 'grab' : 'zoom-in' }}
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
        <img ref={imgRef} key={photos[index]} src={photos[index]} alt={alt} draggable={false}
          className={`max-w-full max-h-full object-contain ${touching ? '' : 'transition-transform duration-150'}`}
          style={{ transform: `translate(${view.x + swipe}px, ${view.y}px) scale(${view.s})` }} />
      </div>

      <div className="absolute top-0 inset-x-0 flex items-center justify-between gap-2 p-3 pointer-events-none"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
        <span className="text-white/80 text-sm tabular-nums px-2">{n > 1 ? `${index + 1} / ${n}` : ''}</span>
        <div className="flex gap-2 pointer-events-auto">
          <button type="button" className={`${ctrl} w-10 h-10`} aria-label={L.zoomOut} disabled={!zoomed} onClick={() => zoomBy(1 / STEP_ZOOM)}><ZoomOut size={20} /></button>
          <button type="button" className={`${ctrl} w-10 h-10`} aria-label={L.zoomIn} disabled={view.s >= MAX_ZOOM} onClick={() => zoomBy(STEP_ZOOM)}><ZoomIn size={20} /></button>
          <button type="button" className={`${ctrl} w-10 h-10`} aria-label={L.close} onClick={close}><X size={22} /></button>
        </div>
      </div>

      {n > 1 && (
        <>
          <button type="button" className={`${ctrl} absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11`} aria-label={L.prev} onClick={() => go(-1)}><ChevronLeft size={26} /></button>
          <button type="button" className={`${ctrl} absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11`} aria-label={L.next} onClick={() => go(1)}><ChevronRight size={26} /></button>
          <div className="absolute bottom-0 inset-x-0 flex justify-center gap-1.5 p-4 pointer-events-none"
            style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
            {photos.map((u, i) => <span key={u} className={`w-2 h-2 rounded-full ${i === index ? 'bg-white' : 'bg-white/35'}`} />)}
          </div>
        </>
      )}
    </div>,
    document.body,
  );
}
